import React, { useEffect, useState } from "react";
import LdsDivider from "../components/LdsDivider";

import { Formik, Form } from "formik";

// component
import LdsValidationError from "../components/LdsValidationError";
import LdsTextField from "../components/LdsTextField";
import LdsButton from "../components/LdsButton";
import { StateDropDown } from "../components/StateDropDown";
import { SpecialityDropDown } from "../components/SpecialtyDropDown";
import { BootstrapAlert } from "../components/BootstrapAlert";
import { ReactLoaderSpinner } from "../components/ReactLoaderSpinner";

// services
import { useGetUserProfileQuery } from "../store/services/authServerApi";
import { useUpdateProfileMutation } from "../store/services/authServerApi";
import { useGetStateQuery } from "../store/services/locationServerApi";
import { useGetSpecialtiesQuery } from "../store/services/specialtiesServerApi";
import { useChangePasswordMutation } from "../store/services/authServerApi";

// import sign up validation schema
import { ProfileSchema } from "../validationSchema/ProfileSchema";
import { ResetPasswordSchema } from "../validationSchema/ResetPasswordSchema";

const Profile = () => {
  console.log("* PROFILE_INIT");

  // state
  const [skipGetUserProfile, setSkipGetUserProfile] = useState(false);
  const [defaultState, setDefaultState] = useState({});
  const [defaultSpeciality, setDefaultSpeciality] = useState({});

  const [
    updateProfile,
    {
      isLoading,
      error: { data: { message: errorMessage } = {} } = {},
      data: { message: successMessage } = {},
    },
  ] = useUpdateProfileMutation();
  console.log(
    "isLoading, errorMessage, successMessage :>> ",
    isLoading,
    errorMessage,
    successMessage
  );

  const {
    isLoading: isUserProfileFetched,
    data: { data: userProfile = [] } = {},
  } = useGetUserProfileQuery(null, {
    skip: skipGetUserProfile,
  });
  const { data: { data: stateList = [] } = {} } = useGetStateQuery();
  const { data: { data: specialityList = [] } = {} } = useGetSpecialtiesQuery();

  const [
    changePassword,
    {
      isLoading: cpLoading,
      error: { data: { message: changePasswordErrorMsg } = {} } = {},
      data: { message: changePasswordSuccessMsg } = {},
    },
  ] = useChangePasswordMutation();

  console.log(
    "cpLoading, changePasswordErrorMsg, changePasswordSuccessMsg :>> ",
    cpLoading,
    changePasswordErrorMsg,
    changePasswordSuccessMsg
  );

  // useEffect
  useEffect(() => {
    // checking for selected state
    if (userProfile?.state && stateList?.length) {
      const { code, name } =
        stateList?.find(({ code }) => code === userProfile?.state) || {};
      setDefaultState({ value: code, label: name });
    }
  }, [userProfile, stateList]);

  useEffect(() => {
    // checking for selected specialty
    if (specialityList?.length && userProfile?.specialty) {
      const { code, name } =
        specialityList?.find(({ code }) => code === userProfile?.specialty) ||
        {};
      setDefaultSpeciality({ value: code, label: name });
    }
  }, [userProfile, specialityList]);

  const handleSubmit = ({ state, specialty, ...rest }) => {
    setSkipGetUserProfile(true);
    updateProfile({
      ...rest,
      state: state?.value,
      specialty: specialty?.value,
    });
  };

  const handleResetPassword = (data) => {
    setSkipGetUserProfile(true);
    changePassword(data);
  };

  return (
    <>
      {isUserProfileFetched && <ReactLoaderSpinner />}
      <main className="container section">
        <div className="lds-layout-container">
          <div className="row">
            <div className="col-sm-12 col-md-8 offset-md-2">
              <h1 className="blue text-uppercase b mb-4">My Profile</h1>
              <p className="mb-5">
                Click the UPDATE button to change your password or profile
                information.
              </p>
              <BootstrapAlert
                variant={errorMessage ? "danger" : "success"}
                message={errorMessage ? errorMessage : successMessage}
              />
              <Formik
                enableReinitialize={true}
                initialValues={{
                  first: "",
                  last: "",
                  address1: "",
                  city: "",
                  state: "",
                  zipCode: "",
                  specialty: "",
                  email: "",
                  // password: "",
                  // confirmPassword: "",
                  ...(userProfile?.first && userProfile), // seq 1
                  ...(userProfile?.state &&
                    defaultState?.value && { state: defaultState }), // seq 2
                  ...(userProfile?.specialty &&
                    defaultSpeciality?.value && {
                      specialty: defaultSpeciality,
                    }), // seq 3
                }}
                validationSchema={ProfileSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="row mb-3">
                      <div className="col-sm-12 col-md-6">
                        <LdsTextField
                          maxLength={100}
                          label="First Name"
                          labelRequired
                          name="first"
                          id="first"
                          error={errors.first && touched.first}
                        />
                        {errors.first && touched.first && (
                          <LdsValidationError id="errfirst">
                            {errors.first}
                          </LdsValidationError>
                        )}
                      </div>
                      <div className="col-sm-12 col-md-6">
                        <LdsTextField
                          maxLength={100}
                          label="Last Name"
                          labelRequired
                          id="last"
                          name="last"
                          error={errors.last && touched.last}
                        />
                        {errors.last && touched.last && (
                          <LdsValidationError id="errlast">
                            {errors.last}
                          </LdsValidationError>
                        )}
                      </div>
                    </div>

                    {/* street address */}
                    <div className="row mb-3">
                      <div className="col-sm-12">
                        <LdsTextField
                          maxLength={500}
                          label="Street Address"
                          labelRequired
                          name="address1"
                          id="address1"
                          error={errors.address1 && touched.address1}
                        />
                        {errors.address1 && touched.address1 && (
                          <LdsValidationError id="erraddress1">
                            {errors.address1}
                          </LdsValidationError>
                        )}
                      </div>
                    </div>

                    {/* city, state, zip */}
                    <div className="row mb-3">
                      <div className="col-sm-12 col-md-5">
                        <LdsTextField
                          maxLength={100}
                          label="City"
                          labelRequired
                          name="city"
                          id="city"
                          error={errors.city && touched.city}
                        />
                        {errors.city && touched.city && (
                          <LdsValidationError id="errcity">
                            {errors.city}
                          </LdsValidationError>
                        )}
                      </div>
                      <div className="col-sm-12 col-md-5">
                        <StateDropDown
                          label="State"
                          labelRequired
                          name="state"
                          error={errors?.state?.value}
                        />
                      </div>
                      <div className="col-sm-12 col-md-2">
                        <LdsTextField
                          maxLength={10}
                          label="Zip Code"
                          labelRequired
                          name="zipCode"
                          id="zipCode"
                          error={errors.zipCode && touched.zipCode}
                        />
                        {errors.zipCode && touched.zipCode && (
                          <LdsValidationError id="errzipCode">
                            {errors.zipCode}
                          </LdsValidationError>
                        )}
                      </div>
                    </div>

                    {/* primary speciality */}
                    <div className="row mb-3">
                      <div className="col-sm-12 col-md-6">
                        <SpecialityDropDown
                          label="Primary Specialty"
                          labelRequired
                          name="specialty"
                          error={errors?.specialty?.value}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-4 col-md-2">
                        <LdsButton
                          disabled={isLoading}
                          type="submit"
                          classes="blue-button mt-3"
                        >
                          Update
                        </LdsButton>
                      </div>
                    </div>
                    <LdsDivider type="secondary" className="mt-4 mb-4" />
                    <div className="row mb-3">
                      <div className="col-sm-12 col-md-6">
                        <LdsTextField
                          maxLength={100}
                          disabled
                          labelRequired
                          label="Email Address"
                          name="email"
                          id="email"
                          type="email"
                          error={errors.email && touched.email}
                        />
                        {errors.email && touched.email && (
                          <LdsValidationError id="erremail">
                            {errors.email}
                          </LdsValidationError>
                        )}
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>

              {/* update password */}
              <LdsDivider type="secondary" className="mb-4 mt-4" />
              <BootstrapAlert
                variant={changePasswordErrorMsg ? "danger" : "success"}
                message={
                  changePasswordErrorMsg
                    ? changePasswordErrorMsg
                    : changePasswordSuccessMsg
                }
              />
              <Formik
                initialValues={{
                  password: "",
                  confirmPassword: "",
                }}
                validationSchema={ResetPasswordSchema}
                onSubmit={handleResetPassword}
              >
                {({ errors, touched }) => (
                  <Form>
                    <div className="row mb-3">
                      <div className="col-sm-12 col-md-6">
                        <LdsTextField
                          maxLength={100}
                          label="Password"
                          labelRequired
                          name="password"
                          id="password"
                          type="password"
                          error={errors.password && touched.password}
                        />
                        {errors.password && touched.password && (
                          <LdsValidationError id="errpassword">
                            {errors.password}
                          </LdsValidationError>
                        )}
                      </div>
                      <div className="col-sm-12 col-md-6">
                        <LdsTextField
                          maxLength={100}
                          label="Confirm Password"
                          labelRequired
                          name="confirmPassword"
                          id="confirmPassword"
                          type="password"
                          error={
                            errors.confirmPassword && touched.confirmPassword
                          }
                        />
                        {errors.confirmPassword && touched.confirmPassword && (
                          <LdsValidationError id="errconfirmPassword">
                            {errors.confirmPassword}
                          </LdsValidationError>
                        )}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-sm-4 col-md-2">
                        <LdsButton
                          disabled={cpLoading}
                          type="submit"
                          classes="blue-button mt-3"
                        >
                          Update
                        </LdsButton>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
              {/* end update password */}
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Profile;
